<template>
  <div class="nuclear-shielding-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$I = I₀·e^(-μx)$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="380" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-nuc">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
           </defs>

           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 13" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="400" />
              <line v-for="i in 9" :key="'h'+i" x1="0" :y1="i*50" x2="600" :y2="i*50" />
           </g>

           <!-- 放射源 (Radium Block) -->
           <g transform="translate(40, 200)">
              <rect x="-30" y="-30" width="60" height="60" fill="#1e293b" stroke="#64748b" stroke-width="3" rx="8" />
              <circle cx="0" cy="0" r="18" fill="#0f172a" />
              <text x="0" y="5" fill="#facc15" font-size="18" text-anchor="middle">☢</text>
           </g>

           <!-- 障碍物组 -->
           <!-- 纸张 -->
           <g transform="translate(200, 50)" :opacity="mode==='alpha'?1:0.6">
              <rect x="-5" y="0" width="10" height="300" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1" />
              <text x="0" y="-15" fill="#f8fafc" font-size="16" font-weight="900" text-anchor="middle">纸张 (Paper)</text>
              <text x="0" y="335" fill="#94a3b8" font-size="12" text-anchor="middle">阻挡 Alpha</text>
           </g>
           
           <!-- 铝板 -->
           <g transform="translate(350, 50)" :opacity="mode==='beta'?1:0.6">
              <rect x="-10" y="0" width="20" height="300" fill="#94a3b8" stroke="#64748b" stroke-width="2" />
              <text x="0" y="-15" fill="#cbd5e1" font-size="16" font-weight="900" text-anchor="middle">铝板 (Aluminum)</text>
              <text x="0" y="335" fill="#64748b" font-size="12" text-anchor="middle">阻挡 Beta</text>
           </g>
           
           <!-- 铅墙 -->
           <g transform="translate(500, 50)" :opacity="mode==='gamma'?1:0.6">
              <rect x="-20" y="0" width="40" height="300" fill="#334155" stroke="#1e293b" stroke-width="3" />
              <text x="0" y="-15" fill="#64748b" font-size="16" font-weight="900" text-anchor="middle">铅砖 (Lead)</text>
              <text x="0" y="335" fill="#475569" font-size="12" text-anchor="middle">削弱 Gamma</text>
           </g>

           <!-- 放射线发射动画流 -->
           <!-- Alpha Stream -->
           <g v-if="mode === 'all' || mode === 'alpha'" filter="url(#glow-nuc)">
              <line v-for="i in 5" :key="'a'+i" x1="60" :y1="160 + i*16" :x2="195" :y2="160 + i*16" stroke="#f43f5e" stroke-width="3" stroke-dasharray="10, 8">
                 <animate attributeName="stroke-dashoffset" from="18" to="0" dur="0.8s" repeatCount="indefinite" />
              </line>
              <text x="80" y="150" fill="#f43f5e" font-size="16" font-weight="bold">α 射线流 (氦核)</text>
           </g>
           
           <!-- Beta Stream -->
           <g v-if="mode === 'all' || mode === 'beta'" filter="url(#glow-nuc)">
              <line v-for="i in 3" :key="'b'+i" x1="60" :y1="250 + i*16" :x2="340" :y2="250 + i*16" stroke="#38bdf8" stroke-width="2" stroke-dasharray="15, 10">
                 <animate attributeName="stroke-dashoffset" from="25" to="0" dur="0.5s" repeatCount="indefinite" />
              </line>
              <!-- 穿过纸张后的余波 -->
              <line v-for="i in 3" :key="'b2-'+i" x1="205" :y1="250 + i*16" x2="340" :y2="250 + i*16" stroke="rgba(56, 189, 248, 0.4)" stroke-width="2" stroke-dasharray="15, 10">
                 <animate attributeName="stroke-dashoffset" from="25" to="0" dur="0.5s" repeatCount="indefinite" />
              </line>
              <text x="80" y="325" fill="#38bdf8" font-size="16" font-weight="bold">β 射线流 (高速电子)</text>
           </g>

           <!-- Gamma Stream -->
           <g v-if="mode === 'all' || mode === 'gamma'" filter="url(#glow-nuc)">
              <path d="M 60 210 Q 70 190, 80 210 T 100 210 T 120 210 T 140 210 T 160 210 T 180 210 T 200 210 T 220 210 T 240 210 T 260 210 T 280 210 T 300 210 T 320 210 T 340 210 T 360 210 T 380 210 T 400 210 T 420 210 T 440 210 T 460 210 T 480 210 T 500 210" fill="none" stroke="#facc15" stroke-width="2" />
              <path d="M 520 210 Q 530 195, 540 210 T 560 210 T 580 210" fill="none" stroke="rgba(250, 204, 21, 0.3)" stroke-width="1.5" />
              <text x="80" y="195" fill="#facc15" font-size="16" font-weight="bold">γ 射线 (高能光子)</text>
           </g>

        </svg>
      </div>

      <!-- 右侧定理解析 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">微观粒子特性解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前选定射线</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'alpha'">
                     <div class="data-row"><span>带电量</span><span class="hl text-red">+2e</span></div>
                     <div class="data-row"><span>静止质量</span><span class="hl">4u (最重)</span></div>
                     <div class="data-row"><span>电离能力</span><span class="hl text-red">极强 (100%)</span></div>
                     <div class="data-row"><span>穿透能力</span><span class="hl">极弱 (几厘米空气或一张纸)</span></div>
                  </template>
                  <template v-if="mode === 'beta'">
                     <div class="data-row"><span>带电量</span><span class="hl text-blue">-1e</span></div>
                     <div class="data-row"><span>静止质量</span><span class="hl">1/1836 u (极轻)</span></div>
                     <div class="data-row"><span>电离能力</span><span class="hl text-blue">中等 (~1%)</span></div>
                     <div class="data-row"><span>穿透能力</span><span class="hl">中等 (几毫米铝板)</span></div>
                  </template>
                  <template v-if="mode === 'gamma'">
                     <div class="data-row"><span>带电量</span><span class="hl text-green">0 (不偏转)</span></div>
                     <div class="data-row"><span>波长</span><span class="hl">皮米级 (极短)</span></div>
                     <div class="data-row"><span>电离能力</span><span class="hl text-green">极弱 (~0.01%)</span></div>
                     <div class="data-row"><span>穿透能力</span><span class="hl">极强 (需十几厘米厚铅砖)</span></div>
                  </template>
                  <template v-if="mode === 'all'">
                     <div class="data-row"><span>综合穿透力</span><span class="hl text-green">γ > β > α</span></div>
                     <div class="data-row"><span>综合电离力</span><span class="hl text-red">α > β > γ</span></div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text"><strong>防护原则：</strong>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>研究对象靶向切换</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="all">衰变混合流对比</el-radio-button>
                 <el-radio-button label="alpha">仅观察 α 射线</el-radio-button>
                 <el-radio-button label="beta">仅观察 β 射线</el-radio-button>
                 <el-radio-button label="gamma">仅观察 γ 射线</el-radio-button>
               </el-radio-group>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NuclearShieldingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'all'
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'alpha') return 'α（阿尔法）射线：氦核构成的重质量轰击粒子束。';
       if (this.mode === 'beta') return 'β（贝塔）射线：接近光速逸出的核内衰变电子流。';
       if (this.mode === 'gamma') return 'γ（伽马）射线：伴随能级跃迁释放的极端高能电磁波。';
       return '放射性衰变的三重产物及其穿透能力阶梯对比。';
    },
    tipDesc() {
       if (this.mode === 'alpha') return '由于 α 粒子质量极大且带2个正电荷，它极易与其他原子碰撞发生电离交出能量。因此一张纸或一层死皮细胞就能将其挡在体外！但切记：绝对不能吸入或食入，否则内照射极其致命。';
       if (this.mode === 'beta') return 'β 粒子质量仅为氦核的七千分之一，运动极快，可以穿透皮肤进入人体稍浅组织。医疗上必须使用铝板或硬塑料等低原子序数材料来屏蔽，不能直接用强金属（以免产生韧致辐射）。';
       if (this.mode === 'gamma') return 'γ 射线没有静止质量也不带电，依靠电磁波的波粒二象性穿梭无阻。只有像“铅墙”或极厚混凝土这样高密度、高原子序数的重装甲，才能凭借光电效应与康普顿散射大幅吸收其能量。';
       return '天然放射源衰变通常射出这三种射线的混合。穿透本领和电离本领呈现完美的负相关：打得越痛的，跑得越近；毫无电离阻力的，却能射穿铅甲。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/Alpha|α|阿尔法/i.test(kp)) this.mode = 'alpha';
      else if (/Beta|β|贝塔/i.test(kp)) this.mode = 'beta';
      else if (/Gamma|γ|伽马/i.test(kp)) this.mode = 'gamma';
      else this.mode = 'all';
    }
  }
}
</script>

<style scoped lang="scss">
.nuclear-shielding-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.6; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #fb7185; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 15px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
